import type { AnswerFeedback } from '../../shared/answers.ts';
import type { SessionEvent } from '../../shared/events.ts';
import { cleanTranscript, SESSION_START_SIGNAL, type ServerMessage, type ToolResponsePart } from '../../shared/live.ts';
import type { CallPlan } from '../../shared/plan/types.ts';
import { getToolSpec } from '../../shared/registry/index.ts';
import { speakingTask } from '../../shared/registry/speaking-task.ts';
import { type SpeechScores, scoresForCoach } from '../../shared/scoring.ts';
import type { TaskKind } from '../../shared/tasks.ts';
import type { Api } from '../lib/api.ts';
import { AnswerRecorder, encodeWav, MIN_VOICED_MS } from '../voice/answer-recorder.ts';
import { every } from '../voice/clock.ts';
import { base64ToInt16 } from '../voice/pcm.ts';
import type { AudioSource } from '../voice/sources.ts';
import type { LiveTransport } from '../voice/transport.ts';

/** A speaking task card's phases (the Lab shows each one). */
export const TASK_PHASES = ['listening', 'too_short', 'processing', 'done', 'error', 'skipped'] as const;

export type CanvasItem = {
  id: string;
  tool: string;
  params: Record<string, unknown>;
  at: number;
  picked?: string;
  // speaking_task progress and result (see TaskView)
  task?: {
    phase: (typeof TASK_PHASES)[number];
    feedback?: AnswerFeedback;
    scores?: SpeechScores;
    /** The review is on the card and the speech scores are still coming. */
    scoresPending?: boolean;
    /** Skip tapped. A card also shows skipped when Gemini cancels it or the call ends; the server counts only this. */
    skippedByUser?: boolean;
  };
};

/** The open speaking task: mic audio goes to the recorder, not to Gemini, until Done. */
export type TaskView = { itemId: string; recording: boolean; voicedMs: number; totalMs: number };
export type TranscriptLine = { id: number; role: 'user' | 'model'; text: string; final: boolean };
export const PHASES = ['idle', 'starting', 'live', 'reconnecting', 'ended', 'error'] as const;
export type Phase = (typeof PHASES)[number];
/** Failures the screen can say how to fix: the site blocked, the OS blocking the browser, another app, no device. */
export const ERROR_KINDS = ['mic_denied', 'mic_system', 'mic_busy', 'mic_missing'] as const;
export type ErrorKind = (typeof ERROR_KINDS)[number];

/**
 * Classified by the error's name, which is stable across browsers while the wording is not. The one exception is the OS
 * blocking the browser: only Chrome says so ("Permission denied by system"); Firefox and Safari report it like a site
 * block, so their users get the site-setting steps.
 */
export function errorKindOf(err: unknown): ErrorKind | null {
  const { name, message } = (err ?? {}) as { name?: string; message?: string };
  if (name === 'NotAllowedError' || name === 'SecurityError')
    // Chrome's wording when the OS privacy setting blocks the browser itself; the site setting can't fix that.
    return /system/i.test(message ?? '') ? 'mic_system' : 'mic_denied';
  if (name === 'NotReadableError') return 'mic_busy';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'mic_missing';
  return null;
}

export type Snapshot = {
  phase: Phase;
  error: string | null;
  errorKind: ErrorKind | null;
  sessionId: string | null;
  plan: CallPlan | null; // what the server planned this call to do (its goal), from the session it created
  items: CanvasItem[];
  transcript: TranscriptLine[];
  modelSpeaking: boolean;
  busyTools: string[];
  task: TaskView | null;
  /** The start is opening the real microphone: a first-time user may be looking at the browser's prompt. */
  askingMic: boolean;
};

export type PlayerLike = {
  unlock(): Promise<void>;
  enqueue(b64: string): void;
  interrupt(): void;
  readonly speaking: boolean;
  onIdle: (() => void) | null;
};

export type ConversationDeps = {
  api: Pick<Api, 'createLiveSession' | 'renewLiveToken' | 'appendEvents' | 'runTool' | 'submitAnswer'>;
  player: PlayerLike;
  transportKind: 'gemini' | 'scripted';
  makeTransport(session: { token?: string; model: string }): Promise<LiveTransport>;
  makeSource(): AudioSource;
  now?: () => number;
};

const MAX_RESUMES = 5;
const RESUMED_RESPONSE_MS = 8_000; // a handle resume that ignores the user this long is treated as stuck
const CONTEXT_LINES = 12;
const NO_RESPONSE_MS = 15_000;
const BATCH_EVENTS = 50; // keeps each request well under the 64 KB keepalive body limit

/**
 * Owns one live session: transport, audio in/out, tool dispatch, transcript, resumption and the event log.
 * React subscribes via subscribe/getSnapshot (see useConversation).
 */
export class Conversation {
  private snap: Snapshot = {
    phase: 'idle',
    error: null,
    errorKind: null,
    sessionId: null,
    plan: null,
    items: [],
    transcript: [],
    modelSpeaking: false,
    busyTools: [],
    task: null,
    askingMic: false,
  };
  private task: {
    callId: string;
    itemId: string;
    params: { kind: TaskKind; prompt: string; script?: string };
    recorder: AnswerRecorder;
    epoch: number;
    mutedCoach: boolean;
    userTyped: boolean;
  } | null = null;
  // A task result sent while reconnecting: a fresh reconnect drops queued sends, so it re-sends this after its context.
  private unsentTaskResult: string | null = null;
  // The server records skips only when the call completes, so its progress still lists these as left.
  private skippedKinds = new Set<TaskKind>();
  private lastTaskPublish = 0;
  private listeners = new Set<() => void>();
  private transport: LiveTransport | null = null;
  private source: AudioSource | null = null;
  private startedAt = 0;
  private events: SessionEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private capTimer: ReturnType<typeof setTimeout> | null = null;
  private resumeHandle: string | undefined;
  private resumes = 0;
  private lineSeq = 0;
  private openLine: { user: number | null; model: number | null } = { user: null, model: null };
  private awaitingFirstAudio = true;
  private lastUserTextAt: number | null = null;
  private eventSeq = 0;
  // Sends made while reconnecting (texts, picks, tool responses); replayed on the resumed socket in order.
  private pending: Array<(t: LiveTransport) => void> = [];
  private flushChain: Promise<void> = Promise.resolve();
  private noResponseTimer: ReturnType<typeof setTimeout> | null = null;
  private turnOpen = false; // the model has started answering and hasn't sent turnComplete yet
  private liveEpoch = 0; // bumped per Live session (start, fresh reconnect); tool calls answer only their own epoch
  private resumeWhenIdle = false; // goAway arrived mid-turn: resume once the turn completes
  private verifyAfterResume = false; // next user input after a handle resume must draw a response
  private resumedTimer: ReturnType<typeof setTimeout> | null = null;
  // Bumped by start() and teardown(). Every await in start/resume re-checks it, so a session that ended (or
  // restarted) while a connect or mic prompt was pending cleans up what that await produced instead of reviving it.
  private gen = 0;
  private readonly now: () => number;
  private readonly deps: ConversationDeps;

  constructor(deps: ConversationDeps) {
    this.deps = deps;
    this.now = deps.now ?? (() => performance.now());
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getSnapshot = () => this.snap;
  /** Resolves once every flush requested so far has finished (e.g. the final flush after end()). */
  whenFlushed = () => this.flushChain;

  private set(patch: Partial<Snapshot>) {
    this.snap = { ...this.snap, ...patch };
    for (const fn of this.listeners) fn();
  }

  /** Time since the Start tap, for the steps before the first audio (the live test prints them). */
  private mark(name: string) {
    this.log('latency', { mark: name, ms: Math.round(this.now() - this.startedAt) });
  }

  private log(type: SessionEvent['type'], data: Record<string, unknown> = {}) {
    this.events.push({ t: Math.max(0, Math.round(this.now() - this.startedAt)), i: this.eventSeq++, type, data });
  }

  /**
   * Sends queued events, one flush at a time. Each flush is bound to the queue and session id current when it was
   * requested, so a late failure re-queues into its own session, never into a restarted one. Network/5xx failures
   * are retried on the next flush; a 4xx batch is dropped so it can't block the queue.
   */
  private flush(all = false): Promise<void> {
    const queue = this.events;
    const id = this.snap.sessionId;
    this.flushChain = this.flushChain.then(() => this.drain(queue, id, all));
    return this.flushChain;
  }

  private async drain(queue: SessionEvent[], id: string | null, all: boolean) {
    let retried = false;
    for (let guard = 0; id && queue.length > 0 && guard < 50; guard++) {
      const batch = queue.splice(0, BATCH_EVENTS);
      try {
        await this.deps.api.appendEvents(id, batch);
      } catch (err) {
        const status = (err as { status?: number }).status ?? 0;
        if (status >= 400 && status < 500) {
          console.warn(`[fuse] dropped ${batch.length} session events: ${(err as Error).message}`);
        } else {
          queue.unshift(...batch);
          // Final flush: one retry in all, then give up rather than spin (the next call's flushes wait behind this).
          if (!all || retried) return;
          retried = true;
          await new Promise((r) => setTimeout(r, 250));
        }
      }
      if (!all) return;
    }
  }

  /** Logs an error if the model produces nothing after user input (a stalled session is otherwise invisible). */
  private watchForResponse(after: string) {
    if (this.noResponseTimer) clearTimeout(this.noResponseTimer);
    const gen = this.gen;
    this.noResponseTimer = setTimeout(() => {
      if (gen === this.gen) this.log('error', { message: `no model response ${NO_RESPONSE_MS} ms after ${after}` });
    }, NO_RESPONSE_MS);
    this.armResumeCheck();
  }

  /**
   * After a handle resume, user input must draw model output within 8 s of the user's *last* input chunk; every
   * chunk restarts the clock, so a long utterance isn't mistaken for a stall. The check ends at the first model output.
   */
  private armResumeCheck() {
    if (this.verifyAfterResume) {
      // A handle resume after a mid-turn state can leave Gemini stuck for good (docs/rca/2026-09-27-live-resume-stall.md).
      if (this.resumedTimer) clearTimeout(this.resumedTimer);
      const gen = this.gen;
      this.resumedTimer = setTimeout(() => {
        if (gen === this.gen && this.snap.phase === 'live') void this.reconnectFresh('no response after resume');
      }, RESUMED_RESPONSE_MS);
    }
  }

  private modelResponded() {
    this.verifyAfterResume = false; // any model output proves the resumed session is alive
    if (this.noResponseTimer) clearTimeout(this.noResponseTimer);
    if (this.resumedTimer) clearTimeout(this.resumedTimer);
    this.noResponseTimer = null;
    this.resumedTimer = null;
  }

  /** Sends now, or holds the send until the resumed socket is up. */
  private deliver(send: (t: LiveTransport) => void) {
    if (!this.transport) return;
    if (this.snap.phase === 'reconnecting') this.pending.push(send);
    else send(this.transport);
  }

  /** Leaves the ended screen (e.g. once setup is done): the finished call's cards and transcript are cleared. */
  dismiss() {
    if (this.snap.phase !== 'ended' && this.snap.phase !== 'error') return;
    this.set({
      phase: 'idle',
      error: null,
      errorKind: null,
      sessionId: null,
      plan: null,
      items: [],
      transcript: [],
      busyTools: [],
    });
  }

  /** Call from the Start tap: audio unlock and mic permission both need the user gesture. */
  async start() {
    if (this.snap.phase !== 'idle' && this.snap.phase !== 'ended' && this.snap.phase !== 'error') return;
    const gen = ++this.gen;
    this.resetSessionState();
    const startLog = this.events; // this start's queue, even if a restart replaces this.events meanwhile
    this.set({
      phase: 'starting',
      error: null,
      errorKind: null,
      sessionId: null,
      plan: null,
      items: [],
      transcript: [],
      busyTools: [],
      askingMic: false,
    });
    try {
      await this.deps.player.unlock();
      if (gen !== this.gen) return;
      this.deps.player.onIdle = () => this.set({ modelSpeaking: false });
      // Read once: an admin's mock plan is fixed when the call starts (useConversation).
      const transportKind = this.deps.transportKind;
      // The mic prompt runs alongside session creation and the connect (about 2 s), not after them: a first-time user
      // reads it while the call connects. A denial fails the start at once; audio before the call is live is dropped.
      const source = this.deps.makeSource();
      this.source = source;
      if (source.kind === 'mic') this.set({ askingMic: true });
      const micReady = source
        .start(
          (chunk) => this.onMicAudio(chunk),
          () => this.transport?.sendAudioStreamEnd(),
        )
        .then(
          () => {
            // Allowed after a cancel: teardown already ran, so close what the prompt just opened.
            if (gen !== this.gen) source.stop();
            else {
              this.mark('mic_ms');
              this.set({ askingMic: false });
            }
            return gen === this.gen;
          },
          (err: unknown) => {
            if (gen === this.gen) this.fail(err);
            // Failed after a cancel, possibly with the stream already open (the worklet failing): close it too.
            else source.stop();
            return false;
          },
        );
      const session = await this.deps.api.createLiveSession(transportKind);
      if (gen !== this.gen) return this.abandon(session.sessionId, startLog);
      this.set({ sessionId: session.sessionId, plan: session.plan ?? null });
      this.log('state', { to: 'starting', transport: transportKind, model: session.model });
      this.mark('session_ms');
      const transport = await this.deps.makeTransport(session);
      if (gen !== this.gen) return;
      this.transport = transport;
      await transport.connect(this.handlers);
      if (gen !== this.gen) return transport.close();
      this.mark('connected_ms');
      // The socket may have closed (setup/quota rejection) while the mic prompt was open; fail() already ran.
      if (!(await micReady) || gen !== this.gen) return;
      this.flushTimer = setInterval(() => void this.flush(), 2000);
      this.capTimer = setTimeout(() => void this.end('time cap'), session.maxMinutes * 60_000);
      this.set({ phase: 'live' });
      this.log('state', { to: 'live', source: source.kind });
      transport.sendText(SESSION_START_SIGNAL);
    } catch (err) {
      if (gen === this.gen) this.fail(err); // a stale start (ended or restarted since) must not touch the new session
    }
  }

  sendText(text: string) {
    const trimmed = text.trim();
    if (!trimmed || !this.transport) return;
    if (this.snap.phase !== 'live' && this.snap.phase !== 'reconnecting') return;
    this.deps.player.interrupt();
    this.closeOpenLine('user'); // keep whatever the user was saying before they started typing
    if (this.task) this.task.userTyped = true; // they want a reply they can hear
    this.pushLine('user', trimmed, true);
    this.log('transcript.user', { text: trimmed, via: 'text' });
    this.deliver((t) => {
      this.lastUserTextAt = this.now();
      t.sendText(trimmed);
      this.watchForResponse('text');
    });
  }

  pick(itemId: string, option: { id: string; label: string }) {
    const item = this.snap.items.find((i) => i.id === itemId);
    if (!item || item.picked || !this.transport) return;
    if (this.snap.phase !== 'live' && this.snap.phase !== 'reconnecting') return;
    this.set({ items: this.snap.items.map((i) => (i.id === itemId ? { ...i, picked: option.id } : i)) });
    this.log('user.pick', { item: itemId, option: option.id });
    this.deps.player.interrupt();
    this.closeOpenLine('user');
    this.pushLine('user', `[picked] ${option.label} (${option.id})`, true); // visible, and part of reconnect context
    this.deliver((t) => {
      this.lastUserTextAt = this.now();
      t.sendText(`[picked] ${option.label} (${option.id})`);
      this.watchForResponse('pick');
    });
  }

  async end(reason = 'user') {
    if (this.snap.phase === 'ended' || this.snap.phase === 'idle') return;
    if (this.snap.phase === 'starting') return this.cancelStart(reason);
    this.finalizeLines(); // lines are otherwise logged on turnComplete, which never comes once we hang up
    this.log('state', { to: 'ended', reason });
    this.teardown();
    this.set({ phase: 'ended', modelSpeaking: false });
    await this.flush(true);
  }

  private resetSessionState() {
    this.startedAt = this.now();
    this.events = [];
    this.eventSeq = 0;
    this.resumeHandle = undefined;
    this.resumes = 0;
    this.awaitingFirstAudio = true;
    this.lastUserTextAt = null;
    this.pending = [];
    this.openLine = { user: null, model: null };
    this.liveEpoch++;
    this.turnOpen = false;
    this.resumeWhenIdle = false;
    this.verifyAfterResume = false;
    this.task = null;
    this.unsentTaskResult = null;
    this.skippedKinds.clear();
  }

  private teardown() {
    this.gen++;
    this.source?.stop();
    this.transport?.close();
    this.deps.player.interrupt();
    if (this.flushTimer) clearInterval(this.flushTimer);
    if (this.capTimer) clearTimeout(this.capTimer);
    this.modelResponded(); // clears the response watchdogs
    this.source = null;
    this.transport = null;
    const task = this.task;
    const phase = task && this.snap.items.find((i) => i.id === task.itemId)?.task?.phase;
    // An unanswered task card must not stay "listening" (with a live Skip) on the ended screen. One being analysed
    // keeps going: finishTask shows its result on the card and drops it otherwise.
    if (task && (phase === 'listening' || phase === 'too_short')) this.setItemTask(task.itemId, { phase: 'skipped' });
    this.task = null;
    if (this.snap.task) this.set({ task: null });
  }

  /**
   * Leaving before the call connected: back home, not the ended screen, since nothing happened to summarise. The
   * server still hears the call ended (here, or from abandon() when the session arrives later), so its post-call
   * catch-up treats the session as over.
   */
  private async cancelStart(reason: string) {
    this.log('state', { to: 'ended', reason: `${reason} while connecting` });
    this.teardown();
    const flushed = this.flush(true);
    this.set({
      phase: 'idle',
      sessionId: null,
      plan: null,
      items: [],
      transcript: [],
      busyTools: [],
      modelSpeaking: false,
      askingMic: false,
    });
    await flushed;
  }

  /**
   * A session created for a start that was cancelled or failed meanwhile (a denied mic fails before the session exists):
   * send that start's log, which had no session to go to. It already ends with `ended` (cancelStart and fail both log
   * it), which closes the session on the server. Sent like a final flush, with its one retry.
   */
  private abandon(sessionId: string, queue: SessionEvent[]) {
    this.flushChain = this.flushChain.then(() => this.drain(queue, sessionId, true));
  }

  private fail(err: unknown) {
    const message = err instanceof Error ? err.message : String(err);
    const errorKind = errorKindOf(err);
    this.log('error', { message, name: (err as { name?: string } | null)?.name });
    const connecting = this.snap.phase === 'starting';
    if (connecting) this.log('state', { to: 'ended', reason: 'failed while connecting' });
    this.teardown();
    // Bound to the session now, so its log still reaches the server once the id is cleared below.
    void this.flush(true);
    // A call that never went live has nothing to summarise: without a session id the screen skips the post-call summary.
    this.set({
      phase: 'error',
      error: message,
      errorKind,
      askingMic: false,
      ...(connecting ? { sessionId: null, plan: null } : {}),
    });
  }

  private handlers = {
    onMessage: (msg: ServerMessage) => this.onMessage(msg),
    onClose: (info: { code?: number; reason?: string }) => void this.onUnexpectedClose(info),
  };

  private async onUnexpectedClose(info: { code?: number; reason?: string }) {
    const why = `connection closed (${info.code ?? '?'}): ${info.reason || 'no reason'}`;
    if (this.snap.phase === 'starting') return this.fail(new Error(why));
    if (this.snap.phase !== 'live') return;
    // Handle resumes are only trusted at turn boundaries; mid-turn they can leave Gemini stuck for good
    // (docs/rca/2026-09-27-live-resume-stall.md).
    // Both reasons are logged: an idle drop that still goes fresh is a missing handle, not a turn left open.
    if (this.turnOpen || !this.resumeHandle) {
      return this.reconnectFresh(why, { turnOpen: this.turnOpen, hasHandle: !!this.resumeHandle });
    }
    await this.resume(why);
  }

  /** Reconnects the same transport with the last resumption handle. Only used at turn boundaries. */
  private async resume(why: string) {
    const transport = this.transport;
    if (!transport) return;
    if (!this.resumeHandle) return this.reconnectFresh(why, { turnOpen: this.turnOpen, hasHandle: false });
    if (this.resumes >= MAX_RESUMES) return this.fail(new Error(`${why}; gave up after ${this.resumes} reconnects`));
    const gen = this.gen;
    this.resumes++;
    this.set({ phase: 'reconnecting' });
    this.log('resume', { why, mode: 'handle', attempt: this.resumes });
    try {
      transport.close();
      await transport.connect(this.handlers, this.resumeHandle);
      if (gen !== this.gen) return transport.close(); // ended while reconnecting
      this.turnOpen = false;
      this.resumeWhenIdle = false; // any handle resume satisfies a pending goAway
      this.verifyAfterResume = true;
      this.set({ phase: 'live' });
      for (const send of this.pending.splice(0)) send(transport);
      this.unsentTaskResult = null; // it was in the replayed queue
    } catch (err) {
      if (gen === this.gen) this.fail(err);
    }
  }

  /**
   * Opens a brand-new Live session (fresh token, no handle) on the same app session and seeds it with the recent
   * transcript. The model loses its internal state but the conversation continues; used when a handle resume
   * can't be trusted.
   */
  private async reconnectFresh(why: string, state: { turnOpen?: boolean; hasHandle?: boolean } = {}) {
    const sessionId = this.snap.sessionId;
    if (!sessionId || !this.transport) return;
    if (this.resumes >= MAX_RESUMES) return this.fail(new Error(`${why}; gave up after ${this.resumes} reconnects`));
    const gen = this.gen;
    this.resumes++;
    this.set({ phase: 'reconnecting', modelSpeaking: false });
    this.log('resume', { why, mode: 'fresh', attempt: this.resumes, ...state });
    this.deps.player.interrupt();
    this.modelResponded();
    this.transport.close();
    const dropped = this.pending.splice(0).length; // typed inputs are already in the transcript we send as context
    try {
      const session = await this.deps.api.renewLiveToken(sessionId);
      if (gen !== this.gen) return;
      const transport = await this.deps.makeTransport(session);
      if (gen !== this.gen) return;
      this.transport = transport;
      this.resumeHandle = undefined;
      this.turnOpen = false;
      this.resumeWhenIdle = false;
      this.liveEpoch++;
      this.finalizeLines();
      await transport.connect(this.handlers);
      if (gen !== this.gen) return transport.close();
      // Anything queued during this reconnect belongs to the old Live session: typed text and picks are already in
      // the transcript sent as context below, and tool responses would answer calls the new session never made.
      const late = this.pending.splice(0).length;
      this.set({ phase: 'live' });
      if (dropped + late) this.log('state', { replayedAsContext: dropped + late });
      transport.sendText(this.contextMessage());
      if (this.unsentTaskResult) {
        transport.sendText(this.unsentTaskResult);
        this.unsentTaskResult = null;
      }
      this.watchForResponse('reconnect context');
    } catch (err) {
      if (gen === this.gen) this.fail(err);
    }
  }

  private contextMessage(): string {
    const lines = this.snap.transcript
      .slice(-CONTEXT_LINES)
      .map((l) => `${l.role === 'user' ? 'User' : 'fuse'}: ${l.text.slice(0, 300)}`);
    const shown = this.snap.items.map(
      (i) => `${i.tool}${typeof i.params.title === 'string' ? ` "${i.params.title}"` : ''}`,
    );
    return [
      '[reconnected] Conversation so far:',
      ...(lines.length ? lines : ['(nothing said yet)']),
      ...(shown.length ? [`Already on screen: ${shown.join(', ')}`] : []),
      ...(this.task
        ? [`The user is still answering your speaking task: "${this.task.params.prompt}". Wait for the result.`]
        : []),
    ].join('\n');
  }

  /**
   * Test hook (?debug=1): drop the live socket as a network failure would, but only in the given state (a turn open,
   * or idle with a resumption handle), and say whether it did. Checked and dropped in one step, so a turn starting or
   * ending between a test's check and its drop can't change which reconnect path runs.
   */
  debugDropWhen(state: 'mid-turn' | 'idle'): boolean {
    const matches = state === 'mid-turn' ? this.turnOpen : !this.turnOpen && !!this.resumeHandle;
    if (matches) this.transport?.simulateDrop?.();
    return matches;
  }

  private onMessage(msg: ServerMessage) {
    if (msg.sessionResumptionUpdate?.newHandle) this.resumeHandle = msg.sessionResumptionUpdate.newHandle;
    if (msg.goAway) {
      this.log('state', { goAway: msg.goAway.timeLeft ?? null });
      // The server gives notice; resume at the next turn boundary, where handle resumes are reliable.
      if (this.turnOpen) this.resumeWhenIdle = true;
      else void this.resume('goAway');
      return;
    }
    if (msg.toolCall?.functionCalls?.length) void this.onToolCalls(msg.toolCall.functionCalls);
    if (msg.toolCallCancellation?.ids?.length) this.onToolCallCancellation(msg.toolCallCancellation.ids);

    const sc = msg.serverContent;
    if (msg.toolCall || sc?.modelTurn || sc?.outputTranscription) {
      this.turnOpen = true;
      this.modelResponded();
    }
    if (!sc) return;
    if (sc.interrupted) {
      // turnOpen stays true: after a barge-in the exchange is still mid-flight until the reply's turnComplete, and a
      // handle resume in that state is unverified (docs/rca). A stuck true only routes a drop to the safe fresh path.
      this.deps.player.interrupt();
      this.set({ modelSpeaking: false });
      this.log('state', { interrupted: true });
    }
    if (sc.inputTranscription?.text) {
      this.appendTranscript('user', sc.inputTranscription.text);
      this.armResumeCheck();
    }
    if (sc.outputTranscription?.text) this.appendTranscript('model', sc.outputTranscription.text);
    for (const part of sc.modelTurn?.parts ?? []) {
      // Empty data = audio stripped from a recorded fixture: nothing will play, so don't enter "speaking".
      if (part.inlineData?.data) {
        // The user is mid-answer: the coach would talk over them, and the recorder would drop those frames.
        if (this.task && this.answering()) {
          if (!this.task.mutedCoach) this.log('state', { task: 'coach audio muted while answering' });
          this.task.mutedCoach = true;
          continue;
        }
        if (this.awaitingFirstAudio) {
          this.awaitingFirstAudio = false;
          this.log('latency', { mark: 'first_audio_ms', ms: Math.round(this.now() - this.startedAt) });
        }
        if (this.lastUserTextAt !== null) {
          this.log('latency', { mark: 'response_ms', ms: Math.round(this.now() - this.lastUserTextAt) });
          this.lastUserTextAt = null;
        }
        this.deps.player.enqueue(part.inlineData.data);
        if (!this.snap.modelSpeaking) this.set({ modelSpeaking: true });
      }
    }
    if (sc.turnComplete) {
      this.turnOpen = false;
      this.finalizeLines();
      if (!this.deps.player.speaking) this.set({ modelSpeaking: false });
      this.log('state', { turnComplete: true });
      this.source?.onModelTurnComplete?.();
      if (this.resumeWhenIdle) {
        this.resumeWhenIdle = false;
        void this.resume('goAway (deferred to turn end)');
      }
    }
  }

  private appendTranscript(role: 'user' | 'model', raw: string) {
    const chunk = cleanTranscript(raw);
    if (!chunk.trim() && !this.openLine[role]) return;
    const openId = this.openLine[role];
    const open = openId === null ? undefined : this.snap.transcript.find((l) => l.id === openId);
    if (open) {
      this.set({
        transcript: this.snap.transcript.map((l) => (l.id === open.id ? { ...l, text: l.text + chunk } : l)),
      });
    } else {
      this.pushLine(role, chunk, false);
    }
  }

  private pushLine(role: 'user' | 'model', text: string, final: boolean) {
    const line = { id: ++this.lineSeq, role, text, final };
    this.openLine[role] = final ? null : line.id;
    this.set({ transcript: [...this.snap.transcript, line] });
  }

  private finalizeLines() {
    this.closeOpenLine('user');
    this.closeOpenLine('model');
  }

  /** Logs the open (still-transcribing) line for a role and marks it final. */
  private closeOpenLine(role: 'user' | 'model') {
    const id = this.openLine[role];
    if (id === null) return;
    this.openLine[role] = null;
    const line = this.snap.transcript.find((l) => l.id === id);
    const text = line?.text.trim() ?? '';
    if (text) this.log(role === 'user' ? 'transcript.user' : 'transcript.model', { text, via: 'voice' });
    this.set({
      transcript: this.snap.transcript
        .map((l) => (l.id === id ? { ...l, text, final: true } : l))
        .filter((l) => l.text.length > 0),
    });
  }

  private async onToolCalls(calls: NonNullable<NonNullable<ServerMessage['toolCall']>['functionCalls']>) {
    const epoch = this.liveEpoch;
    // Everything the model is blocked on goes back in one message: Gemini generates a turn per toolResponse, so
    // answering calls one by one produced empty filler turns.
    const blocking: Array<ToolResponsePart | Promise<ToolResponsePart>> = [];
    const immediateIds: string[] = [];
    for (const call of calls) {
      const id = call.id ?? `call-${Math.random().toString(36).slice(2)}`;
      const name = call.name ?? '';
      const spec = getToolSpec(name);
      this.log('tool.call', { id, name, args: call.args ?? {} });
      if (!spec) {
        blocking.push({ id, name, response: { error: `unknown tool ${name}` } });
        continue;
      }
      if (name === speakingTask.name) {
        const error = this.openTask(id, call.args ?? {});
        if (error) blocking.push({ id, name, response: { error } });
        continue;
      }
      if (spec.execution === 'client') {
        const parsed = spec.params.safeParse(call.args ?? {});
        immediateIds.push(id);
        if (!parsed.success) {
          const error = `invalid arguments: ${parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}`;
          this.log('error', { tool: name, error });
          blocking.push({ id, name, response: { error } });
          continue;
        }
        const item: CanvasItem = { id, tool: name, params: parsed.data as Record<string, unknown>, at: this.now() };
        this.set({ items: [...this.snap.items, item] });
        this.log('render', { id, tool: name });
        blocking.push({ id, name, response: { output: { shown: true } } });
        continue;
      }
      const result = this.runServerTool(id, name, call.args ?? {});
      if (spec.behavior === 'NON_BLOCKING') void result.then((part) => this.answerCalls(epoch, [part]));
      else blocking.push(result);
    }
    if (immediateIds.length) this.log('tool.result', { ids: immediateIds, immediate: true });
    if (!blocking.length) return;
    // Answer in the same tick when nothing is pending, so client acks keep their order relative to later sends.
    const ready = blocking.filter((p): p is ToolResponsePart => !(p instanceof Promise));
    this.answerCalls(epoch, ready.length === blocking.length ? ready : await Promise.all(blocking));
  }

  private answerCalls(epoch: number, parts: ToolResponsePart[]) {
    if (epoch !== this.liveEpoch) {
      this.log('tool.result', { ids: parts.map((p) => p.id), dropped: 'live session replaced by a fresh reconnect' });
      return;
    }
    this.deliver((t) => t.sendToolResponses(parts));
  }

  private async runServerTool(id: string, name: string, args: Record<string, unknown>): Promise<ToolResponsePart> {
    this.set({ busyTools: [...this.snap.busyTools, name] });
    const started = this.now();
    let response: Record<string, unknown>;
    try {
      const res = await this.deps.api.runTool(name, args, this.snap.sessionId ?? '');
      response = 'result' in res ? { output: res.result } : { error: res.error };
    } catch (err) {
      response = { error: err instanceof Error ? err.message : 'tool failed' };
    }
    this.set({ busyTools: this.snap.busyTools.filter((t) => t !== name) });
    this.log('tool.result', { id, name, ms: Math.round(this.now() - started), ok: !('error' in response) });
    const spec = getToolSpec(name);
    // Scheduling only means something for NON_BLOCKING calls (the model has moved on and gets the result later).
    return spec?.behavior === 'NON_BLOCKING'
      ? { id, name, response, scheduling: spec.responseScheduling ?? 'WHEN_IDLE' }
      : { id, name, response };
  }

  // ---- speaking tasks -------------------------------------------------------------------------------------------

  private onMicAudio(chunk: string) {
    if (this.snap.phase === 'starting') return; // the mic opens while connecting; audio counts once the call is live
    const task = this.task;
    if (!task) {
      this.transport?.sendAudio(chunk);
      return;
    }
    const item = this.snap.items.find((i) => i.id === task.itemId);
    if (item?.task?.phase !== 'listening' && item?.task?.phase !== 'too_short') return; // processing: drop
    task.recorder.push(base64ToInt16(chunk), this.deps.player.speaking);
    const t = this.now();
    if (t - this.lastTaskPublish > 200) {
      this.lastTaskPublish = t;
      this.publishTask();
    }
  }

  /** Recording an answer right now (not waiting for it to start, not analysing it, not after they typed). */
  private answering(): boolean {
    const task = this.task;
    if (!task || task.userTyped || task.recorder.state !== 'recording') return false;
    const phase = this.snap.items.find((i) => i.id === task.itemId)?.task?.phase;
    return phase === 'listening' || phase === 'too_short';
  }

  private publishTask() {
    const task = this.task;
    this.set({
      task: task
        ? {
            itemId: task.itemId,
            recording: task.recorder.state === 'recording',
            voicedMs: Math.round(task.recorder.voicedMs),
            totalMs: Math.round(task.recorder.totalMs),
          }
        : null,
    });
  }

  private setItemTask(itemId: string, task: CanvasItem['task']) {
    this.set({ items: this.snap.items.map((i) => (i.id === itemId ? { ...i, task } : i)) });
  }

  /** Opens a speaking task from the model's NON_BLOCKING call. Returns an error for the model if it can't. */
  private openTask(callId: string, args: Record<string, unknown>): string | null {
    const parsed = speakingTask.params.safeParse(args);
    if (!parsed.success) return `invalid arguments: ${parsed.error.issues.map((i) => i.message).join('; ')}`;
    if (this.task) return 'a speaking task is already open; wait for its result';
    // Only a read-aloud is read: a script shown on anything else would turn free speech into reading.
    const { script: _dropped, ...unscripted } = parsed.data;
    const params = parsed.data.kind === 'read_aloud' ? parsed.data : unscripted;
    const recorder = new AnswerRecorder(() => void this.finishTask(), MIN_VOICED_MS[params.kind]);
    this.task = {
      callId,
      itemId: callId,
      params,
      recorder,
      epoch: this.liveEpoch,
      mutedCoach: false,
      userTyped: false,
    };
    // The mic stops feeding Gemini until the task closes; tell it the stream paused so it flushes buffered audio.
    this.deliver((t) => t.sendAudioStreamEnd());
    this.set({
      items: [
        ...this.snap.items,
        { id: callId, tool: speakingTask.name, params, at: this.now(), task: { phase: 'listening' } },
      ],
    });
    this.log('render', { id: callId, tool: speakingTask.name });
    this.publishTask();
    this.cueAnswer(this.task);
    return null;
  }

  // Fixture sources "speak" the answer once the model has been quiet for a moment, like a person would; starting
  // while the coach is still talking would lose the start of the answer (the recorder drops those frames).
  private cueAnswer(task: NonNullable<Conversation['task']>) {
    if (!this.source?.onTaskStarted) return;
    let quietSince = this.now();
    // Fixture sources only: on the worker clock, so a hidden tab doesn't hold the clip back (see voice/clock.ts).
    const stop = every(100, () => {
      if (this.task !== task) return stop();
      if (this.deps.player.speaking) quietSince = this.now();
      else if (this.now() - quietSince >= 1500) {
        stop();
        this.source?.onTaskStarted?.();
      }
    });
  }

  /** Done tapped (or the length cap hit). Too short keeps listening; otherwise analyse and answer the model. */
  async finishTask() {
    const task = this.task;
    if (!task || !this.snap.sessionId) return;
    const item = this.snap.items.find((i) => i.id === task.itemId);
    // Already sent. Done only shows while listening, so this is defensive: it keeps a second submit from ever racing.
    if (item?.task?.phase === 'processing' || item?.task?.scoresPending) return;
    if (!task.recorder.longEnough) {
      // A short answer is usually a false start, so it starts over. An introduction cut short is still the start of
      // one (pauses don't count as voice, so 14 s of talking can be under 10 s voiced): it keeps what was said and
      // carries on at the next word, so the pause while they read the card isn't scored. At the length cap there is
      // no room left, so that starts over too.
      if (task.params.kind === 'free_speech' && !task.recorder.atCap) task.recorder.pauseUntilVoice();
      else task.recorder.reset();
      this.setItemTask(task.itemId, { phase: 'too_short' });
      this.log('state', { task: 'too_short' });
      this.publishTask();
      return;
    }
    this.setItemTask(task.itemId, { phase: 'processing' });
    this.log('state', { task: 'processing', answerMs: Math.round(task.recorder.totalMs) });
    let response: Record<string, unknown>;
    try {
      const res = await this.deps.api.submitAnswer(
        { sessionId: this.snap.sessionId, taskId: task.itemId, ...task.params },
        encodeWav(task.recorder.pcm()),
        // The review lands seconds before the scores: show it now. The coach still waits for both, so it talks once.
        (feedback) => this.setItemTask(task.itemId, { phase: 'done', feedback, scoresPending: true }),
      );
      if ('error' in res) throw new Error(res.error);
      this.setItemTask(task.itemId, { phase: 'done', feedback: res.feedback, scores: res.scores });
      // The call ended meanwhile: the card shows the result and the server has recorded it (in the assessment set too,
      // by the session's stage), but it's not part of this call's transcript or the coach's context.
      if (this.task !== task) return;
      const said = res.feedback.transcript.trim();
      if (said) {
        // Part of the transcript like any user turn, so reconnect context and the post-call updater see it.
        this.closeOpenLine('user');
        this.pushLine('user', said, true);
        this.log('transcript.user', { text: said, via: 'task' });
      }
      this.log('tool.result', {
        id: task.callId,
        name: speakingTask.name,
        ...(res.feedback.unreviewed ? { reviewed: false } : { relevance: res.feedback.relevance }),
      });
      const extra = {
        ...(res.scores ? { speechScores: scoresForCoach(res.scores) } : {}),
        ...(res.progress
          ? {
              progress: {
                ...res.progress,
                kindsNotYetDone: res.progress.kindsNotYetDone.filter((k) => !this.skippedKinds.has(k)),
              },
            }
          : {}),
      };
      response = {
        output: res.feedback.unreviewed
          ? { contentReview: 'unavailable: react to the speech scores only', ...extra }
          : { transcript: res.feedback.transcript, feedback: res.feedback, ...extra },
      };
    } catch (err) {
      this.setItemTask(task.itemId, { phase: 'error' });
      if (this.task !== task) return;
      this.log('error', { tool: speakingTask.name, message: err instanceof Error ? err.message : String(err) });
      response = { error: 'could not analyse the answer; ask them to try again or move on' };
    }
    this.closeTask(task, response);
  }

  /** User chose not to answer. */
  skipTask() {
    const task = this.task;
    if (!task) return;
    this.setItemTask(task.itemId, { phase: 'skipped', skippedByUser: true });
    // The kind lets the post-call completion count the skip in the assessment set, so a refusal never blocks it.
    this.log('state', { task: 'skipped', kind: task.params.kind });
    this.skippedKinds.add(task.params.kind);
    this.closeTask(task, { output: { skipped: true } });
  }

  private closeTask(task: NonNullable<Conversation['task']>, response: Record<string, unknown>) {
    if (this.task !== task) return;
    this.task = null;
    this.source?.onTaskClosed?.();
    this.publishTask();
    this.lastUserTextAt = this.now();
    const asText = `[task result] ${JSON.stringify({ question: task.params.prompt, ...response })}`;
    if (this.snap.phase === 'reconnecting') this.unsentTaskResult = asText;
    if (task.epoch !== this.liveEpoch) {
      // A fresh reconnect replaced the Live session, which never saw this call id: send the result as a message.
      this.deliver((t) => t.sendText(asText));
    } else {
      this.deliver((t) =>
        t.sendToolResponses([{ id: task.callId, name: speakingTask.name, response, scheduling: 'WHEN_IDLE' }]),
      );
    }
    this.watchForResponse('speaking task');
  }

  /** Gemini cancelled calls (the user interrupted the turn that made them). An open task closes without an answer. */
  private onToolCallCancellation(ids: string[]) {
    this.log('state', { cancelled: ids });
    const task = this.task;
    if (!task || !ids.includes(task.callId)) return;
    this.setItemTask(task.itemId, { phase: 'skipped' });
    this.task = null;
    this.source?.onTaskClosed?.();
    this.publishTask();
  }
}
